import React, { useState, useEffect } from 'react';
import HullEquipmentStatusMatrix from "../HullEquipmentStatusMatrix";
import ProjectProgressSCurve from './CPRM/ProjectProgressSCurve';
import ProjectProgressOverview from './CPRM/ProjectProgressOverview';
import EquipmentOrderingStatus from './CPRM/EquipmentOrderingStatus';
import GlassCard from './CPRM/GlassCard';
import { getRequest } from '../services/apiService';

const hullEquipmentMockData = [
  { ser: 1, eqpt: 'Bow Thruster', delivery: 'green', installation: 'yellow', stw: 'red', wot: 'red', hats: 'red', remarks: 'Installation in progress' },
  { ser: 2, eqpt: 'Stern Tube', delivery: 'green', installation: 'green', stw: 'yellow', wot: 'red', hats: 'red', remarks: 'STW planned next month' },
  { ser: 3, eqpt: 'Rudder', delivery: 'green', installation: 'green', stw: 'green', wot: 'yellow', hats: 'red', remarks: 'WOT in progress' },
  { ser: 4, eqpt: 'Anchor Windlass', delivery: 'yellow', installation: 'red', stw: 'red', wot: 'red', hats: 'red', remarks: 'Awaiting delivery' },
  { ser: 5, eqpt: 'Steering Gear', delivery: 'green', installation: 'green', stw: 'green', wot: 'green', hats: 'yellow', remarks: 'HATS scheduled' },
  { ser: 6, eqpt: 'Shafting', delivery: 'yellow', installation: 'yellow', stw: 'red', wot: 'red', hats: 'red', remarks: 'Partial delivery received' },
  { ser: 7, eqpt: 'Bilge Pump', delivery: 'green', installation: 'green', stw: 'green', wot: 'green', hats: 'green', remarks: 'Completed' },
];

const CPRMDashboard = () => {
  const [selectedProject, setSelectedProject] = useState(1);
  const [selectedShip, setSelectedShip] = useState(12);
  const [scurveData, setSCurveData] = useState({}); // This state will now be managed internally by ProjectProgressSCurve or could be removed if not used elsewhere
  const [shipProgressData, setShipProgressData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(4);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [showEquipmentDetails, setShowEquipmentDetails] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('HULL');
  const [equipmentList] = useState([]);
  const [loadingShipProgress, setLoadingShipProgress] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
   const [projects, setProjects] = useState([]);
   const [selectedParentProject, setSelectedParentProject] = useState('1');
   const [ships, setShips] = useState([]);
   const [selectedShipId, setSelectedShipId] = useState(12);


    useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('/master/projects/');
        if (response?.data) {
          setProjects(response.data);
          // Ensure default project is selected if available
          const defaultProject = response.data.find(p => p.id === 1);
          if (defaultProject) {
            setSelectedProject(1);
          }
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);
  
  useEffect(() => {
    const fetchShips = async () => {
      if (!selectedProject) return;
      try {
        const response = await getRequest(`/master/projects/?parent_id=${selectedProject}`);
        if (response?.data) {
          setShips(response.data);
          // Set default ship to 12 if available, else first ship
          const defaultShip = response.data.find(s => s.id === 12);
          if (defaultShip) {
            setSelectedShip(defaultShip.id);
          } else {
            setSelectedShip(response.data[0]?.id || null);
          }
        }
      } catch (error) {
        console.error("Error fetching ships:", error);
        setShips([]);
        setSelectedShip(null);
      }
    };
    fetchShips();
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    setLoadingShipProgress(true);
    getRequest(`/transaction/api/ship-progress/?project_id=${selectedProject}`)
      .then(response => setShipProgressData(response?.data || response || []))
      .catch(() => setShipProgressData([]))
      .finally(() => setLoadingShipProgress(false));
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject || !selectedDepartment) {
      setEquipmentData([]);
      return;
    }
    setLoadingEquipment(true);
    let url = `/transaction/api/equipment-status/${selectedProject}/?department_id=${selectedDepartment}`;
    if (selectedEquipment.length > 0) {
      url += selectedEquipment.map(id => `&equipment_id=${id}`).join('');
    }
    getRequest(url)
      .then(response => setEquipmentData(response?.data || response || []))
      .catch(() => setEquipmentData([]))
      .finally(() => setLoadingEquipment(false));
  }, [selectedProject, selectedDepartment, selectedEquipment]);

  useEffect(() => {
    getRequest('master/departments/')
      .then(res => setDepartments(res?.data || []))
      .catch(() => setDepartments([]));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-slate-50 to-indigo-100 flex flex-col py-4 px-1 md:px-6">
      <header className="sticky top-0 z-30 w-full max-w-full mx-auto mb-8 rounded-2xl shadow-lg bg-white/70 backdrop-blur-md border border-slate-100 px-4 md:px-10 py-6 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-indigo-900 tracking-tight mb-1">CPRM Dashboard</h1>
          <p className="text-base text-indigo-600">Comprehensive Project Resource Management Overview</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2 bg-white p-2 rounded-full border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2">
              <label htmlFor="project-select" className="text-xs font-semibold text-gray-700">Project:</label>
               <select
                id="project-select"
                value={selectedProject}
                onChange={e => {
                  setSelectedProject(Number(e.target.value));
                  setSelectedShip(null); // Reset ship on project change
                }}
                className="appearance-none bg-transparent px-3 py-1 rounded-full border-none text-sm font-medium focus:ring-2 focus:ring-indigo-400 focus:outline-none min-w-[120px]"
              >
                {projects.map(proj => (
                  <option key={proj.id} value={proj.id}>{proj.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="ship-select" className="text-xs font-semibold text-gray-700">Ship:</label>
              <select
                id="ship-select"
                value={selectedShip || ''}
                onChange={e => setSelectedShip(Number(e.target.value))}
                className="appearance-none bg-transparent px-3 py-1 rounded-full border-none text-sm font-medium focus:ring-2 focus:ring-indigo-400 focus:outline-none min-w-[120px]"
                disabled={!selectedProject}
              >
                {ships.map(ship => (
                  <option key={ship.id} value={ship.id}>{ship.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full max-w-full mx-auto flex-1">
        <div className="flex flex-col gap-8">
          <GlassCard title="Project Progress Over Time (S-Curve)" chartId="scurve-chart">
            <ProjectProgressSCurve 
              selectedProject={selectedProject}
              selectedShip={selectedShip} // Passed selectedShip here
            />
          </GlassCard>

          <GlassCard title="Project Progress Overview" data={shipProgressData} chartId="progress-chart">
            <ProjectProgressOverview data={shipProgressData} />
          </GlassCard>

          <GlassCard
            title="Equipment Ordering & Delivery Status"
            data={equipmentData.equipment}
            chartId="equipment-chart"
          >
            <EquipmentOrderingStatus
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              data={equipmentData.equipment || []}
              loading={loadingEquipment}
              departments={departments}
              selectedDepartment={selectedDepartment}
              setSelectedDepartment={setSelectedDepartment}
              equipmentList={equipmentList}
              selectedEquipment={selectedEquipment}
              setSelectedEquipment={setSelectedEquipment}
            />
          </GlassCard>

          <GlassCard title="Hull Equipment Status Matrix" data={hullEquipmentMockData} chartId="hull-matrix-chart">
            <HullEquipmentStatusMatrix data={hullEquipmentMockData} projectId={selectedShip} />
          </GlassCard>
        </div>
      </main>
    </div>
  );
};

export default CPRMDashboard;